import React, { useMemo } from 'react';
import { RRule } from 'rrule';
import Icons from './Icons';

const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const WEEKDAY_LABELS = { SU: 'Sun', MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat' };
const WEEKDAY_FULL_LABELS = { SU: 'Sunday', MO: 'Monday', TU: 'Tuesday', WE: 'Wednesday', TH: 'Thursday', FR: 'Friday', SA: 'Saturday' };
const POSITION_LABELS = { 1: 'First', 2: 'Second', 3: 'Third', 4: 'Fourth', '-1': 'Last' };
const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const RRULE_WEEKDAY = { SU: RRule.SU, MO: RRule.MO, TU: RRule.TU, WE: RRule.WE, TH: RRule.TH, FR: RRule.FR, SA: RRule.SA };
const RRULE_FREQ = { DAILY: RRule.DAILY, WEEKLY: RRule.WEEKLY, MONTHLY: RRule.MONTHLY, YEARLY: RRule.YEARLY };

// Parse a 'YYYY-MM-DD' string as a local calendar date (no timezone shift)
const parseLocalDate = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Given a date, work out its "Nth weekday of the month" position (1-4, or -1 for last)
const getMonthlyPosition = (dateStr) => {
  const date = parseLocalDate(dateStr);
  const day = date.getDate();
  const weekday = WEEKDAY_CODES[date.getDay()];
  const nextOccurrence = new Date(date);
  nextOccurrence.setDate(day + 7);
  const isLast = nextOccurrence.getMonth() !== date.getMonth();
  return { position: isLast ? -1 : Math.ceil(day / 7), weekday };
};

export const defaultRecurrence = (startDate) => {
  const date = startDate ? parseLocalDate(startDate) : new Date();
  const { position, weekday } = getMonthlyPosition(startDate || `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`);
  return {
    freq: 'WEEKLY',
    interval: 1,
    byweekday: [weekday],
    monthlyMode: 'day',
    bymonthday: date.getDate(),
    bysetpos: position,
    bysetposWeekday: weekday,
    bymonth: date.getMonth() + 1,
    endMode: 'count',
    count: 10,
    until: ''
  };
};

// Convert the editor's UI state into the { freq, interval, byweekday, ... }
// payload shape the backend's RRULE builder expects.
export const toRecurrencePayload = (recurrence) => {
  const payload = {
    freq: recurrence.freq,
    interval: Math.max(1, parseInt(recurrence.interval, 10) || 1)
  };

  if (recurrence.freq === 'WEEKLY') {
    payload.byweekday = recurrence.byweekday;
  }

  if (recurrence.freq === 'MONTHLY' || recurrence.freq === 'YEARLY') {
    if (recurrence.monthlyMode === 'day') {
      payload.bymonthday = parseInt(recurrence.bymonthday, 10);
    } else {
      payload.byweekday = [recurrence.bysetposWeekday];
      payload.bysetpos = parseInt(recurrence.bysetpos, 10);
    }
  }

  if (recurrence.freq === 'YEARLY') {
    payload.bymonth = parseInt(recurrence.bymonth, 10);
  }

  if (recurrence.endMode === 'until') {
    payload.until = recurrence.until;
  } else {
    payload.count = Math.max(1, parseInt(recurrence.count, 10) || 1);
  }

  return payload;
};

// Build a preview RRule purely client-side, for the live human-readable summary.
// Mirrors the backend's buildRecurrenceDates logic (kept in sync intentionally -
// both use the same `rrule` library and the same option shape).
const buildPreviewRule = (recurrence, startDate) => {
  if (!startDate) return null;
  const payload = toRecurrencePayload(recurrence);

  const options = {
    freq: RRULE_FREQ[payload.freq],
    interval: payload.interval,
    dtstart: parseLocalDate(startDate)
  };
  if (payload.byweekday) options.byweekday = payload.byweekday.map(code => RRULE_WEEKDAY[code]);
  if (payload.bymonthday) options.bymonthday = payload.bymonthday;
  if (payload.bysetpos) options.bysetpos = payload.bysetpos;
  if (payload.bymonth) options.bymonth = payload.bymonth;
  if (payload.count) options.count = Math.min(payload.count, 104);
  if (payload.until) options.until = parseLocalDate(payload.until);

  return new RRule(options);
};

const RecurrenceEditor = ({ recurrence, onChange, startDate }) => {
  const update = (patch) => onChange({ ...recurrence, ...patch });

  const toggleWeekday = (code) => {
    const current = recurrence.byweekday || [];
    update({
      byweekday: current.includes(code)
        ? current.filter(c => c !== code)
        : [...current, code].sort((a, b) => WEEKDAY_CODES.indexOf(a) - WEEKDAY_CODES.indexOf(b))
    });
  };

  const { preview, previewError } = useMemo(() => {
    try {
      const rule = buildPreviewRule(recurrence, startDate);
      if (!rule) return { preview: '', previewError: null };
      return { preview: rule.toText(), previewError: null };
    } catch (err) {
      return { preview: '', previewError: 'Set a valid pattern to see a preview' };
    }
  }, [recurrence, startDate]);

  const isSingularInterval = parseInt(recurrence.interval, 10) === 1;
  const intervalLabel = {
    DAILY: isSingularInterval ? 'day' : 'days',
    WEEKLY: isSingularInterval ? 'week' : 'weeks',
    MONTHLY: isSingularInterval ? 'month' : 'months',
    YEARLY: isSingularInterval ? 'year' : 'years'
  }[recurrence.freq];

  return (
    <div className="border border-gray-200 rounded-md p-4 space-y-4 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
          <select
            value={recurrence.freq}
            onChange={(e) => update({ freq: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="YEARLY">Yearly</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Repeat every</label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="1"
              value={recurrence.interval}
              onChange={(e) => update({ interval: e.target.value })}
              className="w-20 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">{intervalLabel}</span>
          </div>
        </div>
      </div>

      {recurrence.freq === 'WEEKLY' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">On these days</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_CODES.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => toggleWeekday(code)}
                className={`w-11 h-11 rounded-full text-sm font-medium transition-colors ${
                  (recurrence.byweekday || []).includes(code)
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-600 hover:border-gray-400'
                }`}
              >
                {WEEKDAY_LABELS[code]}
              </button>
            ))}
          </div>
        </div>
      )}

      {(recurrence.freq === 'MONTHLY' || recurrence.freq === 'YEARLY') && (
        <div className="space-y-3">
          {recurrence.freq === 'YEARLY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <select
                value={recurrence.bymonth}
                onChange={(e) => update({ bymonth: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {MONTH_LABELS.map((label, i) => (
                  <option key={label} value={i + 1}>{label}</option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              checked={recurrence.monthlyMode === 'day'}
              onChange={() => update({ monthlyMode: 'day' })}
              className="text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 flex items-center space-x-2">
              <span>On day</span>
              <input
                type="number"
                min="1"
                max="31"
                disabled={recurrence.monthlyMode !== 'day'}
                value={recurrence.bymonthday}
                onChange={(e) => update({ bymonthday: e.target.value })}
                className="w-16 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              />
            </span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              checked={recurrence.monthlyMode === 'position'}
              onChange={() => update({ monthlyMode: 'position' })}
              className="text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 flex items-center space-x-2 flex-wrap">
              <span>On the</span>
              <select
                disabled={recurrence.monthlyMode !== 'position'}
                value={recurrence.bysetpos}
                onChange={(e) => update({ bysetpos: e.target.value })}
                className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              >
                {Object.entries(POSITION_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              <select
                disabled={recurrence.monthlyMode !== 'position'}
                value={recurrence.bysetposWeekday}
                onChange={(e) => update({ bysetposWeekday: e.target.value })}
                className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              >
                {WEEKDAY_CODES.map((code) => (
                  <option key={code} value={code}>{WEEKDAY_FULL_LABELS[code]}</option>
                ))}
              </select>
            </span>
          </label>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ends</label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              checked={recurrence.endMode === 'count'}
              onChange={() => update({ endMode: 'count' })}
              className="text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 flex items-center space-x-2">
              <span>After</span>
              <input
                type="number"
                min="1"
                max="104"
                disabled={recurrence.endMode !== 'count'}
                value={recurrence.count}
                onChange={(e) => update({ count: e.target.value })}
                className="w-20 border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              />
              <span>occurrences</span>
            </span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              checked={recurrence.endMode === 'until'}
              onChange={() => update({ endMode: 'until' })}
              className="text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 flex items-center space-x-2">
              <span>On date</span>
              <input
                type="date"
                disabled={recurrence.endMode !== 'until'}
                value={recurrence.until}
                onChange={(e) => update({ until: e.target.value })}
                min={startDate}
                className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              />
            </span>
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-1">Recurring events are capped at 104 occurrences.</p>
      </div>

      <div className="flex items-start space-x-2 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
        <span className="text-primary-500 mt-0.5">{Icons.calendar('w-4 h-4')}</span>
        <p className="text-sm text-primary-800">
          {previewError || preview || 'Set a start date to see a preview'}
        </p>
      </div>
    </div>
  );
};

export default RecurrenceEditor;
